import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Button, ButtonTypes } from '../../../common/Button';
import { Loader } from '../../App/components/Loader';
import { setUserRole } from '../../../firebase/classrooms';
import { showToast } from '../actions/toast';

/**
 * Add Instructor or admin (admin only). Enter a registered user's email + pick a role → setUserRole
 * callable sets/clears their custom claim. The change only takes effect for that user after their
 * token refreshes, so we surface a "must re-login" note on success.
 */
const AddRoleModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'success' | 'error', message }

  function validate({ email }) {
    const errors = {};
    if (!email || email.trim().length === 0) {
      errors.email = t('AddRoleModal.EnterEmail', {
        defaultValue: 'Enter the user’s email.'
      });
    }
    return errors;
  }

  async function onSubmit({ email, role }) {
    setBusy(true);
    setNotice(null);
    let submitError;
    try {
      await setUserRole(email.trim(), role || 'instructor');
      dispatch(showToast('Toast.RoleUpdated', 3500));
      setNotice({
        type: 'success',
        message: t('AddRoleModal.Success', {
          defaultValue:
            'Role updated. The user must sign out and back in for it to take effect.'
        })
      });
    } catch (e) {
      const code = (e && e.code) || '';
      if (code.includes('not-found')) {
        dispatch(showToast('Toast.RoleUserNotFound', 4000));
        submitError = {
          email: t('AddRoleModal.NotFound', {
            defaultValue: 'No registered user with that email.'
          })
        };
      } else {
        dispatch(showToast('Toast.RoleError', 4000));
        submitError = {
          email: t('AddRoleModal.Error', {
            defaultValue: 'Could not update the role. Please try again.'
          })
        };
      }
    } finally {
      setBusy(false);
    }
    return submitError;
  }

  if (busy) {
    return (
      <div className="classroom-modal" style={{ minHeight: '6rem' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="classroom-modal">
      <Form
        validate={validate}
        onSubmit={onSubmit}
        initialValues={{ role: 'instructor' }}
      >
        {({ handleSubmit, submitting, touched, errors }) => (
          <form className="classroom-form" onSubmit={handleSubmit}>
            <Field name="email">
              {(field) => (
                <React.Fragment>
                  <label htmlFor="role-email">
                    {t('AddRoleModal.EmailLabel', {
                      defaultValue: 'User email'
                    })}
                  </label>
                  <input
                    id="role-email"
                    type="email"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    placeholder={t('AddRoleModal.EmailPlaceholder', {
                      defaultValue: 'student@example.com'
                    })}
                    {...field.input}
                  />
                </React.Fragment>
              )}
            </Field>
            <Field name="role">
              {(field) => (
                <React.Fragment>
                  <label htmlFor="role-select">
                    {t('AddRoleModal.RoleLabel', { defaultValue: 'Role' })}
                  </label>
                  <select id="role-select" {...field.input}>
                    <option value="instructor">
                      {t('AddRoleModal.RoleInstructor', {
                        defaultValue: 'Instructor'
                      })}
                    </option>
                    <option value="admin">
                      {t('AddRoleModal.RoleAdmin', { defaultValue: 'Admin' })}
                    </option>
                    <option value="student">
                      {t('AddRoleModal.RoleStudent', {
                        defaultValue: 'Student (remove access)'
                      })}
                    </option>
                  </select>
                </React.Fragment>
              )}
            </Field>
            <Button type={ButtonTypes.SUBMIT} disabled={submitting}>
              {t('AddRoleModal.Submit', { defaultValue: 'Set role' })}
            </Button>
            {touched.email && errors.email && (
              <span className="form-error" aria-live="polite">
                {errors.email}
              </span>
            )}
            {notice && (
              <span
                className={
                  notice.type === 'success' ? 'form-success' : 'form-error'
                }
                aria-live="polite"
              >
                {notice.message}
              </span>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};

export default AddRoleModal;
